import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CircularProgress from "@mui/material/CircularProgress";
import DateRange from "../components/DateRange";
import StateTable from "../components/StateTable";
import { Map } from "../components/Map";

const Container = styled.div`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const ActivityBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;

  h2 {
    font-size: 1.8rem;
  }
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    width: 250px;
    font-weight: 600;
    color: #013566;
    margin: 0;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    padding: 0.5rem;
    border: none;
    outline: none;
    border-radius: 12px;
    background-color: #013566;
    color: white;
  }
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }

  p {
    width: 250px;
  }
`;

const TileElement = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const TileCount = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  color: #013566;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    color: #013566;
  }
`;

const CampaignIdSelect = styled.div`
  margin-bottom: 1.5rem;

  p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #013566;
  }

  select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;

    &:hover {
      border-color: #013566;
    }

    &:focus {
      outline: none;
      border-color: #013566;
      box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: #013566;
  font-size: 0.9rem;
`;

const ConversionSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #013566 0%, #012a4a 100%);
  border-radius: 8px;
  color: white;
`;

const ConversionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
`;

const ConversionValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
`;

const ExpandableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 1px solid #eee;
  border-top: none;
  padding: 1rem;
  background: #fafbfc;
  border-radius: 0 0 8px 8px;
  margin-top: -1px;
`;

const ExpandableRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  p {
    width: 250px;
    margin: 0;
  }
`;

interface Props {
  startDate: Date | null;
  endDate: Date | null;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
}

const SingleProject: React.FC<Props> = ({
  startDate,
  endDate,
  handleEndDateChange,
  handleStartDateChange,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openedList, setOpenedList] = useState<any[]>([]);
  const projectName = localStorage.getItem("projectName");
  const [currentPage, setCurrentPage] = useState(1);
  const submissionsPerPage = 10;
  const [subDomains, setSubDomains] = useState<any[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);
  const [selectedFormType, setSelectedFormType] = useState<string>("all");

  const indexOfLastSubmission = currentPage * submissionsPerPage;
  const indexOfFirstSubmission = indexOfLastSubmission - submissionsPerPage;
  const filteredSubmissions =
    stats?.total_submissions?.filter((submission: any) => {
      if (selectedFormType === "all") return true;
      return submission?.form_type === selectedFormType;
    }) || [];

  const currentSubmissions = filteredSubmissions.slice(
    indexOfFirstSubmission,
    indexOfLastSubmission
  );

  const totalPages = Math.max(
    1,
    Math.ceil((filteredSubmissions.length || 0) / submissionsPerPage)
  );

  const token = localStorage.getItem("token");

  const fetchStats = async () => {
    setLoading(true);
    setCurrentPage(1);
    setOpenedList([]);
    setSelectedFormType("all");

    const formattedStartDate = startDate
      ? startDate.toISOString().split("T")[0]
      : "";
    const formattedEndDate = endDate
      ? endDate.toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0];

    try {
      if (!id) return;
      const response = await axios.get(API_ENDPOINTS.PROJECT_DETAILS(id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });
      setStats(response.data);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const fetchSubDomains = async () => {
      setCurrentPage(1);
      setOpenedList([]);
      try {
        const response = await axios.get(API_ENDPOINTS.SUBDOMAINS(id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setSubDomains(response.data);
      } catch (error: any) {
        console.error(error);
      }
    };

    fetchSubDomains();
  }, [id]);

  useEffect(() => {
    if (!selectedDomainId) return;

    if (selectedDomainId === "reset") {
      setSelectedDomainId(null);
      setStats(null);
      fetchStats();
      return;
    }

    const fetchSelectedCampaignStats = async () => {
      setLoading(true);
      setCurrentPage(1);
      setOpenedList([]);
      setSelectedFormType("all");

      const formattedStartDate = startDate
        ? startDate.toISOString().split("T")[0]
        : "";
      const formattedEndDate = endDate
        ? endDate.toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      try {
        const response = await axios.post(
          API_ENDPOINTS.SUBDOMAIN_DETAILS,
          {
            start_date: formattedStartDate,
            end_date: formattedEndDate,
            subdomain_id: selectedDomainId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setStats(response.data);
      } catch (error: any) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSelectedCampaignStats();
  }, [selectedDomainId, startDate, endDate]);

  useEffect(() => {
    fetchStats();
  }, [token, startDate, endDate]);

  // Ensure current page is valid when data changes
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  console.log(stats);
  return (
    <Container>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        handleEndDateChange={handleEndDateChange}
        handleStartDateChange={handleStartDateChange}
      />

      {projectName !== "Welcome Home Owner" && projectName !== "MRG" && (
        <CampaignIdSelect>
          <p>Campaign ID</p>
          <select
            value={selectedDomainId ?? ""}
            onChange={(e) => setSelectedDomainId(e.target.value)}
          >
            <option value="" disabled>
              Select a domain
            </option>
            {subDomains.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.domain_key} ({domain.domain_id})
              </option>
            ))}
            <option value="reset">Reset</option>
          </select>
        </CampaignIdSelect>
      )}

      {loading ? (
        <ActivityBox>
          <CircularProgress />
        </ActivityBox>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
            marginTop: "2rem",
          }}
        >
          <h1>{projectName}</h1>
          <Tile>
            <h2>Page Views</h2>
            <TileHeader>
              <h2>Views</h2>
              <h2>Count</h2>
            </TileHeader>

            <TileSection>
              {stats?.page_views?.total_views > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <TileRow>
                    <TileElement>Total Views</TileElement>
                    <TileCount>{stats?.page_views?.total_views}</TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Mobile Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[0]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Desktop Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[1]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Tablet Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[2]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Unique Views</TileElement>
                    <TileCount>{stats?.page_views?.unique_views}</TileCount>
                  </TileRow>

                  {stats?.page_views?.domains?.length > 0 && (
                    <>
                      <TileRow
                        onClick={() => {
                          if (openedList.includes("domains")) {
                            setOpenedList(
                              openedList.filter((item) => item !== "domains")
                            );
                          } else {
                            setOpenedList([...openedList, "domains"]);
                          }
                        }}
                        style={
                          openedList?.includes("domains")
                            ? { cursor: "pointer", borderBottom: "none" }
                            : {
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }
                        }
                      >
                        <TileElement>Domains</TileElement>
                        <TileCount>
                          {openedList?.includes("domains") ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                            </svg>
                          )}
                        </TileCount>
                      </TileRow>
                      {openedList?.includes("domains") && (
                        <ExpandableSection>
                          {stats?.page_views?.domains?.map(
                            (domain: any, index: string) => (
                              <ExpandableRow key={index}>
                                <TileElement style={{ fontSize: ".9rem" }}>
                                  {domain?.domain}
                                </TileElement>
                                <TileCount style={{ fontSize: "1rem" }}>
                                  {domain?.total}
                                </TileCount>
                              </ExpandableRow>
                            )
                          )}
                        </ExpandableSection>
                      )}
                    </>
                  )}

                  {stats?.page_views?.utm_campaigns?.length > 0 && (
                    <>
                      <TileRow
                        onClick={() => {
                          if (openedList.includes("utm_campaigns")) {
                            setOpenedList(
                              openedList.filter(
                                (item) => item !== "utm_campaigns"
                              )
                            );
                          } else {
                            setOpenedList([...openedList, "utm_campaigns"]);
                          }
                        }}
                        style={
                          openedList?.includes("utm_campaigns")
                            ? { cursor: "pointer", borderBottom: "none" }
                            : {
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }
                        }
                      >
                        <TileElement>Utm Campaigns</TileElement>
                        <TileCount>
                          {openedList?.includes("utm_campaigns") ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                            </svg>
                          )}
                        </TileCount>
                      </TileRow>
                      {openedList?.includes("utm_campaigns") && (
                        <ExpandableSection>
                          {stats?.page_views?.utm_campaigns?.map(
                            (campaign: any, index: string) => (
                              <ExpandableRow key={index}>
                                <TileElement style={{ fontSize: ".9rem" }}>
                                  {campaign?.utm_campaign}
                                </TileElement>
                                <TileCount style={{ fontSize: "1rem" }}>
                                  {campaign?.total}
                                </TileCount>
                              </ExpandableRow>
                            )
                          )}
                        </ExpandableSection>
                      )}
                    </>
                  )}

                  {stats?.page_views?.utm_ids?.length > 0 && (
                    <>
                      <TileRow
                        onClick={() => {
                          if (openedList.includes("utm_ids")) {
                            setOpenedList(
                              openedList.filter((item) => item !== "utm_ids")
                            );
                          } else {
                            setOpenedList([...openedList, "utm_ids"]);
                          }
                        }}
                        style={
                          openedList?.includes("utm_ids")
                            ? { cursor: "pointer", borderBottom: "none" }
                            : {
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }
                        }
                      >
                        <TileElement>Utm Ids</TileElement>
                        <TileCount>
                          {openedList?.includes("utm_ids") ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                            </svg>
                          )}
                        </TileCount>
                      </TileRow>
                      {openedList?.includes("utm_ids") && (
                        <ExpandableSection>
                          {stats?.page_views?.utm_ids?.map(
                            (id: any, index: string) => (
                              <ExpandableRow key={index}>
                                <TileElement style={{ fontSize: ".9rem" }}>
                                  {id?.utm_id}
                                </TileElement>
                                <TileCount style={{ fontSize: "1rem" }}>
                                  {id?.total}
                                </TileCount>
                              </ExpandableRow>
                            )
                          )}
                        </ExpandableSection>
                      )}
                    </>
                  )}

                  {stats?.page_views?.utm_mediums?.length > 0 && (
                    <>
                      <TileRow
                        onClick={() => {
                          if (openedList.includes("utm_mediums")) {
                            setOpenedList(
                              openedList.filter(
                                (item) => item !== "utm_mediums"
                              )
                            );
                          } else {
                            setOpenedList([...openedList, "utm_mediums"]);
                          }
                        }}
                        style={
                          openedList?.includes("utm_mediums")
                            ? { cursor: "pointer", borderBottom: "none" }
                            : {
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }
                        }
                      >
                        <TileElement>Utm Mediums</TileElement>
                        <TileCount>
                          {openedList?.includes("utm_mediums") ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                            </svg>
                          )}
                        </TileCount>
                      </TileRow>
                      {openedList?.includes("utm_mediums") && (
                        <ExpandableSection>
                          {stats?.page_views?.utm_mediums?.map(
                            (medium: any, index: string) => (
                              <ExpandableRow key={index}>
                                <TileElement style={{ fontSize: ".9rem" }}>
                                  {medium?.utm_medium}
                                </TileElement>
                                <TileCount style={{ fontSize: "1rem" }}>
                                  {medium?.total}
                                </TileCount>
                              </ExpandableRow>
                            )
                          )}
                        </ExpandableSection>
                      )}
                    </>
                  )}

                  {stats?.page_views?.utm_sources?.length > 0 && (
                    <>
                      <TileRow
                        onClick={() => {
                          if (openedList.includes("utm_sources")) {
                            setOpenedList(
                              openedList.filter(
                                (item) => item !== "utm_sources"
                              )
                            );
                          } else {
                            setOpenedList([...openedList, "utm_sources"]);
                          }
                        }}
                        style={
                          openedList?.includes("utm_sources")
                            ? { cursor: "pointer", borderBottom: "none" }
                            : {
                                cursor: "pointer",
                                borderBottom: "1px solid #eee",
                              }
                        }
                      >
                        <TileElement>Utm Sources</TileElement>
                        <TileCount>
                          {openedList?.includes("utm_sources") ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0l-160 160c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 141.2 160 448c0 17.7 14.3 32 32 32s32-14.3 32-32l0-306.7L329.4 246.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-160-160z" />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 384 512"
                            >
                              <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8 224 64c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
                            </svg>
                          )}
                        </TileCount>
                      </TileRow>
                      {openedList?.includes("utm_sources") && (
                        <ExpandableSection>
                          {stats?.page_views?.utm_sources?.map(
                            (source: any, index: string) => (
                              <ExpandableRow key={index}>
                                <TileElement style={{ fontSize: ".9rem" }}>
                                  {source?.utm_source}
                                </TileElement>
                                <TileCount style={{ fontSize: "1rem" }}>
                                  {source?.total}
                                </TileCount>
                              </ExpandableRow>
                            )
                          )}
                        </ExpandableSection>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p>No page view data available</p>
              )}
            </TileSection>
          </Tile>

          {projectName !== "MRG" && (
            <>
              <h2>Page Views by State Map</h2>
              {stats?.by_state?.page_views?.usa && (
                <Map stateData={stats?.by_state?.page_views?.usa} />
              )}

              <Tile>
                <h2>
                  Page Views by State. Remaining views are from outside US
                </h2>
                {stats?.by_state?.page_views?.usa && (
                  <>
                    {/* <USMap stateData={stats.by_state.page_views.usa} /> */}
                    <StateTable
                      stateData={stats.by_state.page_views.usa}
                      dataType="views"
                    />
                  </>
                )}
              </Tile>
            </>
          )}

          <Tile>
            <h2>Clicks</h2>
            <TileHeader>
              <h2>Clicked Element</h2>
              <h2>Count</h2>
            </TileHeader>

            <TileSection>
              <TileRow>
                <TileElement>Unique Clicks</TileElement>
                <TileCount>{stats?.clicks?.unique_clicks}</TileCount>
              </TileRow>
              {stats?.clicks?.all?.length > 0 ? (
                stats.clicks.all.map((click: any, index: number) => (
                  <TileRow key={index}>
                    <TileElement>{click?.element}</TileElement>
                    <TileCount>{click?.total_clicks}</TileCount>
                  </TileRow>
                ))
              ) : (
                <p>No click data available.</p>
              )}
            </TileSection>
          </Tile>

          <Tile>
            <h2>Form Submissions</h2>

            {projectName === "Welcome Home Owner" && (
              <CampaignIdSelect>
                <p>Form Type Filter</p>
                <select
                  value={selectedFormType}
                  onChange={(e) => {
                    setSelectedFormType(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All Form Types</option>
                  {stats?.total_submissions
                    ?.reduce((types: string[], submission: any) => {
                      const formType = submission?.form_type;
                      if (formType && !types.includes(formType)) {
                        types.push(formType);
                      }
                      return types;
                    }, [])
                    .map((formType: string) => (
                      <option key={formType} value={formType}>
                        {formType}
                      </option>
                    ))}
                </select>
              </CampaignIdSelect>
            )}

            <TileHeader>
              {/* <h2>Name</h2>
              <h2>Email</h2> */}
              <h2>Submission Date</h2>
              <h2>Campaign ID</h2>
              {(projectName === "Welcome Home Owner" ||
                projectName === "MRG") && <h2>Form Type</h2>}
              <h2>Status</h2>
              <h2>Error Description</h2>
            </TileHeader>

            <TileSection>
              {currentSubmissions.length > 0 ? (
                currentSubmissions.map((submission: any, index: number) => (
                  <TileRow key={index}>
                    {/* <TileElement>
                      {submission?.form_data?.name || "No Name Provided"}
                    </TileElement>
                    <TileElement>
                      {submission?.form_data?.email || "No Email Provided"}
                    </TileElement> */}
                    <TileElement>
                      {submission?.created_at
                        ? new Date(submission.created_at).toLocaleString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )
                        : "No Date Provided"}
                    </TileElement>
                    <TileElement>
                      {submission?.domain_id && submission?.domain_key
                        ? `${submission.domain_id} - ${submission.domain_key}`
                        : "No Campaign ID Provided"}
                    </TileElement>
                    {(projectName === "Welcome Home Owner" ||
                      projectName === "MRG") && (
                      <TileElement>
                        {submission?.form_type || "No Form Type Provided"}
                      </TileElement>
                    )}
                    <TileElement
                      style={{
                        color: submission?.is_failed ? "#dc3545" : "#28a745",
                        fontWeight: "600",
                      }}
                    >
                      {submission?.is_failed ? "Failed" : "Success"}
                    </TileElement>
                    <TileElement>
                      {submission?.is_failed && submission?.error_description
                        ? submission.error_description
                        : submission?.is_failed
                        ? "No error description provided"
                        : "-"}
                    </TileElement>
                  </TileRow>
                ))
              ) : (
                <p>No form submissions available.</p>
              )}

              {/* Pagination Controls */}
              <PaginationContainer>
                <PaginationButton
                  $disabled={currentPage <= 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  ← Previous
                </PaginationButton>
                <PageInfo>
                  Page {currentPage} of {totalPages}
                </PageInfo>
                <PaginationButton
                  $disabled={currentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  Next →
                </PaginationButton>
              </PaginationContainer>

              <ConversionSection>
                <ConversionTitle>
                  Conversions - engaged website visitors / total visitors
                </ConversionTitle>
                {/* <div
                   style={{
                     fontSize: "0.8rem",
                     opacity: 0.8,
                     marginBottom: "0.5rem",
                   }}
                 >
                   Data flow starts 06.02.2025
                 </div> */}
                <ConversionValue>
                  {stats?.page_views?.conversions}%
                </ConversionValue>
              </ConversionSection>
            </TileSection>
          </Tile>

          {projectName !== "Welcome Home Owner" && projectName !== "MRG" && (
            <Tile>
              <h2>
                Form Submissions by State. Remaining submissions are from
                outside US
              </h2>
              {stats?.by_state?.form_submissions?.usa && (
                <StateTable
                  stateData={stats.by_state.form_submissions.usa}
                  dataType="submissions"
                />
              )}
            </Tile>
          )}
        </div>
      )}

      <GoBack onClick={() => navigate(-1)}>Go Back</GoBack>
    </Container>
  );
};

export default SingleProject;
